import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress,
  Button, TextField, MenuItem, IconButton, Tooltip, Stack, Typography,
} from '@mui/material';
import { Add, Refresh, EditOutlined, DeleteOutline, SellOutlined, PersonOutline, PublicOutlined } from '@mui/icons-material';
import {
  PageHeader, StatCard, FilterBar, FilterSpacer, TableSurface, headerCellSx, bodyRowSx,
  TableEmptyState, ErrorState, TypePill, StatusDot,
} from '../../components/common/pageKit';
import { priceListService, PriceList } from '../../services/priceListService';
import { linkNav } from '../../utils/linkNav';

const fmtDate = (d?: string | null) => (d ? String(d).slice(0, 10) : '—');
const money = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PriceListsPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PriceList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');

  const load = useCallback(() => {
    setLoading(true);
    priceListService.list()
      .then((r) => { setRows(r.priceLists || []); setError(null); })
      .catch((e) => setError(e?.message || 'Failed to load price lists'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (typeFilter !== 'ALL' && r.price_type !== typeFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [r.list_name, r.product_name, r.product_code, r.customer_name]
      .some((v) => (v || '').toLowerCase().includes(q));
  }), [rows, search, typeFilter]);

  const stats = useMemo(() => ({
    total: rows.length,
    customer: rows.filter((r) => r.price_type === 'CUSTOMER').length,
    general: rows.filter((r) => r.price_type === 'GENERAL').length,
  }), [rows]);

  const del = async (id: number) => {
    if (!window.confirm('Delete this price list entry?')) return;
    try { await priceListService.remove(id); load(); } catch (e: any) { setError(e?.message || 'Delete failed'); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Price Lists"
        subtitle="Customer and general rate master — resolves the line rate on quotations and sales orders."
        actions={(
          <>
            <Button variant="outlined" startIcon={<Refresh />} onClick={load} sx={{ mr: 1 }}>Refresh</Button>
            <Button variant="contained" startIcon={<Add />} {...linkNav('/sales/price-lists/new', navigate)}>New Price List</Button>
          </>
        )}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        <StatCard title="Total Entries" value={stats.total} icon={<SellOutlined />} color="#2563eb" />
        <StatCard title="Customer-Specific" value={stats.customer} icon={<PersonOutline />} color="#7c3aed" />
        <StatCard title="General Lists" value={stats.general} icon={<PublicOutlined />} color="#0891b2" />
      </Box>

      <FilterBar>
        <TextField size="small" placeholder="Search product / customer / name" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260 }} />
        <TextField size="small" select label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="ALL">All types</MenuItem>
          <MenuItem value="CUSTOMER">Customer-specific</MenuItem>
          <MenuItem value="GENERAL">General</MenuItem>
        </TextField>
        <FilterSpacer />
        <Typography variant="caption" color="text.secondary">{filtered.length} of {rows.length}</Typography>
      </FilterBar>

      {error ? <ErrorState message={error} onRetry={load} /> : (
        <TableSurface>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Name', 'Type', 'Product', 'Customer', 'Effective', 'Min Qty', 'Rate', 'Priority', 'Status', ''].map((h) => (
                    <TableCell key={h} sx={headerCellSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ border: 0, py: 6 }}><CircularProgress /></TableCell></TableRow>
                ) : !filtered.length ? (
                  <TableRow><TableCell colSpan={10} sx={{ border: 0 }}><TableEmptyState title="No price lists" hint="Create a price list to drive rate resolution on sales documents." /></TableCell></TableRow>
                ) : filtered.map((r) => (
                  <TableRow key={r.id} hover sx={bodyRowSx}>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{r.list_name || '—'}</Typography></TableCell>
                    <TableCell><TypePill label={r.price_type} tint={r.price_type === 'CUSTOMER' ? '#7c3aed' : '#0891b2'} /></TableCell>
                    <TableCell>{r.product_name || r.product_code || `#${r.product_id}`}</TableCell>
                    <TableCell>{r.price_type === 'CUSTOMER' ? (r.customer_name || (r.customer_id ? `#${r.customer_id}` : '—')) : 'All customers'}</TableCell>
                    <TableCell><Typography variant="caption">{fmtDate(r.effective_from)} → {fmtDate(r.effective_to)}</Typography></TableCell>
                    <TableCell>{Number(r.min_qty || 0)}{r.uom ? ` ${r.uom}` : ''}</TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{money(r.special_price)}</Typography></TableCell>
                    <TableCell>{r.priority}</TableCell>
                    <TableCell><StatusDot label={r.is_active ? 'Active' : 'Inactive'} tone={r.is_active ? 'success' : 'neutral'} /></TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit"><IconButton size="small" {...linkNav(`/sales/price-lists/${r.id}/edit`, navigate)}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" onClick={() => del(r.id)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}
    </Box>
  );
};

export default PriceListsPage;
